/**
 * Constructs the public URL for an object in a public S3 bucket.
 * Assumes the standard virtual-hosted style URL format.
 * Adjust the URL format if your bucket uses path-style access or a specific region endpoint.
 *
 * @param {string} bucketName - The name of the public S3 bucket.
 * @param {string} key - The path (key) of the object within the bucket.
 * @returns {string} The full public URL to the S3 object.
 */
export function getPublicS3Url(bucketName, key) {
  if (!bucketName || !key) {
    console.warn("Bucket name or key is missing for getPublicS3Url");
    // Return a default placeholder or empty string if appropriate
    return "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png";
  }
  // Ensure the key doesn't have a leading slash which can cause issues
  const cleanKey = key.startsWith("/") ? key.substring(1) : key;
  // Standard public URL format (adjust if needed, e.g., add region)
  return `https://${bucketName}.s3.amazonaws.com/${cleanKey}`;
}

/**
 * Retrieves public S3 URLs for a given set of keys, using localStorage for caching.
 *
 * @param {Object.<string, string>} imageskeyRoutes - An object mapping semantic keys to S3 object keys.
 * @param {string} itemName - A unique identifier for the cache key (e.g., 'MobileLayout').
 * @param {number} [cacheDurationMinutes=60] - How long the cache is valid in minutes.
 * @returns {Promise<Object.<string, string>>} A promise that resolves to an object mapping semantic keys to their public S3 URLs.
 */
export function getImagesUrls(
  imageskeyRoutes,
  itemName,
  cacheDurationMinutes = 60
) {
  const cacheKeyBase = `${itemName}PublicImageUrls`;
  const timestampKey = `${cacheKeyBase}Timestamp`;
  const cacheDurationMillis = cacheDurationMinutes * 60 * 1000;

  const now = new Date().getTime();
  const cachedTimestamp = localStorage.getItem(timestampKey);
  const cachedUrlsJson = localStorage.getItem(cacheKeyBase);

  let imageUrls = {};

  if (
    cachedTimestamp &&
    cachedUrlsJson &&
    now - parseInt(cachedTimestamp, 10) < cacheDurationMillis
  ) {
    try {
      imageUrls = JSON.parse(cachedUrlsJson);
      let needsUpdate = false;

      for (const key in imageskeyRoutes) {
        if (!imageUrls[key]) {
          console.log(`Cache miss for key: ${key}. Generating public URL.`);
          const objectKey = imageskeyRoutes[key];
          imageUrls[key] = objectKey
            ? getPublicS3Url(process.env.REACT_APP_S3_BUCKET_NAME, objectKey)
            : "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png"; // Default for empty keys
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        localStorage.setItem(cacheKeyBase, JSON.stringify(imageUrls));
      }

      console.log("Returning image URLs from cache (potentially updated).");
      return imageUrls;
    } catch (e) {
      console.error("Error parsing cached image URLs, fetching fresh.", e);
    }
  }

  console.log("Cache invalid or missing, generating fresh public URLs.");
  imageUrls = {};

  for (const key in imageskeyRoutes) {
    const objectKey = imageskeyRoutes[key];
    if (objectKey && objectKey.length > 0) {
      imageUrls[key] = getPublicS3Url(
        process.env.REACT_APP_S3_BUCKET_NAME,
        objectKey
      );
    } else {
      imageUrls[key] =
        "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png";
    }
  }

  try {
    localStorage.setItem(cacheKeyBase, JSON.stringify(imageUrls));
    localStorage.setItem(timestampKey, now.toString());
  } catch (e) {
    console.error("Failed to save image URLs to localStorage.", e);
  }

  return imageUrls;
}
